/**
 * Centralized environment access for the portal.
 *
 * Every `import.meta.env` read funnels through here, mirroring how the engine
 * funnels all `process.env` reads through src/config/env.js.
 *
 * IMPORTANT — nothing secret belongs in here. Vite inlines every `VITE_*` value
 * into the client bundle at build time, so anything placed in a VITE_ variable
 * is public. Secrets stay server-side (see server.js / Railway variables).
 */

function optional(value: string | undefined, fallback: string): string {
  return value != null && value.trim() !== '' ? value.trim() : fallback;
}

/**
 * Base path/URL for engine API calls.
 *
 * Default `/api` is a SAME-ORIGIN relative path, served by our own Express
 * server (server.js), which proxies through to the engine. This is deliberate:
 * the engine issues its auth cookies with `SameSite=Strict`, and because
 * `up.railway.app` is on the Public Suffix List, the portal and engine Railway
 * domains are *cross-site*. A browser would silently drop those cookies on any
 * direct cross-origin call — auth would never work. Going through our own
 * origin keeps the engine's strict cookie posture intact and needs no CORS.
 *
 * Setting VITE_API_URL to an absolute URL switches to direct mode, which is
 * only viable once portal and engine are same-site (e.g. voleto.app +
 * api.voleto.app) and the origin is on the engine's CORS allowlist.
 */
export const API_BASE_URL = optional(import.meta.env.VITE_API_URL, '/api').replace(/\/+$/, '');

/** True when API calls go to our own origin (proxy mode). */
export const IS_PROXIED_API = !/^https?:\/\//i.test(API_BASE_URL);

/**
 * Stripe publishable key.
 *
 * Public by design: it identifies the Stripe account and can only tokenize a
 * card, never move money. The SECRET key lives on the engine and never reaches
 * a browser. Empty when unset, which checkout surfaces as a clear "payments
 * unavailable" state rather than a silently broken card form.
 */
export const STRIPE_PUBLISHABLE_KEY = optional(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, '');

export const APP_ENV = optional(import.meta.env.VITE_APP_ENV, import.meta.env.MODE);

export const IS_PRODUCTION = import.meta.env.PROD;

/**
 * Social profile URLs, shown in the footer.
 *
 * Empty by default, and the footer renders NOTHING for an empty one. Voleto has no
 * social accounts yet, and linking to profiles that do not exist would be exactly the
 * fabricated content task 44 stripped out of the site. Set a VITE_SOCIAL_* variable and
 * the icon appears; until then the row is simply absent.
 */
export const SOCIAL_LINKS: { label: string; url: string }[] = [
  { label: 'Instagram', url: optional(import.meta.env.VITE_SOCIAL_INSTAGRAM, '') },
  { label: 'TikTok', url: optional(import.meta.env.VITE_SOCIAL_TIKTOK, '') },
  { label: 'X', url: optional(import.meta.env.VITE_SOCIAL_X, '') },
  { label: 'YouTube', url: optional(import.meta.env.VITE_SOCIAL_YOUTUBE, '') },
].filter((s) => s.url !== '');

/** Where "Contact" points. A real, monitored address rather than a page that does not exist. */
export const CONTACT_EMAIL = optional(import.meta.env.VITE_CONTACT_EMAIL, 'hello@voleto.app');
